import { StudentTab } from "./StudentTab";
import { Alert, Box, Card, IconButton, Typography } from "@mui/material";
import { LinearProgress, ListBase, Pagination, useGetOne, useGetRecordId, useRecordContext } from "react-admin";
import { CreateManualInvoiceButton } from "../../buttons/CreateManualInvoiceButton";
import { InvoicesList } from "../../lists/InvoicesList";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { Row } from "../../misc/Row";
import type { Student } from "../../providers/studentsProvider.js";
import { LoadingIndicator } from "../../misc/LoadingIndicator.js";
import { overviewCardStyle } from "../../misc/backofficeTheme";
import EditIcon from "@mui/icons-material/EditOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { PrepaidCreditsDialog } from "../../dialogs/PrepaidCreditsDialog";
import { useState } from "react";
import { DrivingSchoolAccounts } from "./AccountBalance";
import { AppliedCreditsDialog } from "../../dialogs/AppliedCreditsDialog";
import { moneyFormatter } from "../../misc/Money";
import { grants } from "../../backoffice.access_control";
import { EmailSammelquittungButton } from "../../buttons/EmailSammelquittungButton";
import { Column } from "../../misc/Column";
import { CreatePaymentButton } from "../../buttons/CreatePaymentButton";
import { FeatureToggleGreyOut } from "../../misc/FeatureToggleGreyOut";
import { CreateStudentCostOverviewPdfMenu } from "../../buttons/CreateStudentCostOverviewPdfMenu";
import { useDialog } from "../../hooks/useDialog";
import { StudentDunningStatusDialog } from "../../dialogs/StudentDunningStatusDialog";
import { ActivateDunningDialog } from "../../dialogs/ActivateDunningDialog";
import { InkassoBlockingDialog } from "../../dialogs/InkassoBlockingDialog";
import { DateTime } from "luxon";
import { getDunningStatusLabel } from "../../utils/dunning";
import { useThemeColor } from "../../hooks/useThemeColor";

export function StudentInvoicesList() {
  const studentId = useGetRecordId();
  const student = useRecordContext<Student>();
  const { data: latestStudent } = useGetOne<Student>("students", { id: student?.id ?? "" }, { enabled: !!student });
  const dunningStudent = latestStudent ?? student;
  const isInCollections = dunningStudent?.dunningProcess?.status === "handedOverToPairFinance";
  const [openPrepaidCreditDialog, setOpenPrepaidCreditDialog] = useState<boolean>(false);
  const [openFreeCreditDialog, setOpenFreeCreditDialog] = useState<boolean>(false);
  const [openCollectionsInfoDialog, setOpenCollectionsInfoDialog] = useState<boolean>(false);
  const { dialogProps: dunningDialogProps, openDialog: openFullDunningDialog } = useDialog();
  const { dialogProps: activateDunningDialogProps, openDialog: openActivateDunningDialog } = useDialog();
  const { data } = useGetOne("backendUser", { id: student?.id ?? "" }, { enabled: !!student });
  const accounts =
    typeof data?.payload?.accounts === "object"
      ? ((data.payload.accounts[student?.drivingSchoolId ?? ""] ?? Object.values(data.payload.accounts)[0]) as
          | DrivingSchoolAccounts
          | undefined)
      : undefined;
  const totalCreditsAmount =
    parseFloat(accounts?.free_credits.balance.amount ?? "0.00") +
    parseFloat(accounts?.start_credits.balance.amount ?? "0.00") +
    parseFloat(accounts?.credits_contingents?.balance?.amount ?? "0.00");
  const totalExpenditure = (student?.balances.paid ?? Number.NaN) / 100;
  const monetaryFlow = data?.payload?.monetary_flow;

  if (!student) {
    return <LoadingIndicator />;
  }
  return (
    <StudentTab>
      <ListBase resource="b2cInvoices" filter={{ studentId, excludeType: ["PREPAID_CREDITS"] }}>
        <Box data-testid="StudentInvoices-scrollArea" sx={{ maxWidth: "100%", minWidth: 0, overflowX: "auto" }}>
          <Card
            sx={{
              ...overviewCardStyle,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              gap: "20px 16px",
              alignItems: "flex-start",
              padding: "20px 30px",
            }}
          >
            <_InvoiceKPI amount={totalExpenditure} name="Gesamtausgaben" isLoading={Number.isNaN(totalExpenditure)} />
            <_InvoiceKPI
              amount={parseFloat(accounts?.balance?.balance?.amount ?? "0")}
              name="Saldo"
              redForNegativeValues
            />
            {["purchaseOnAccount", "externalBankTransfer"].includes(student.paymentStrategy) && (
              <_InvoiceKPI amount={student.balances.open / 100} name="Betrag offene Rechnungen" />
            )}

            <FeatureToggleGreyOut
              featureActive={student.featureToggles?.canHavePrepaidCredits ?? true}
              disabledReason={
                student.billingProvider === "datapart" ? "Nicht verfügbar für Datapart Kunden" : undefined
              }
            >
              <_InvoiceKPI
                amount={parseFloat(accounts?.prepaid_credits?.balance?.amount ?? "0")}
                name="Prepaid Guthaben"
                actionIcon="info"
                handleModalOpen={() => setOpenPrepaidCreditDialog(true)}
              />
            </FeatureToggleGreyOut>

            <FeatureToggleGreyOut
              featureActive={student.featureToggles?.canHaveFreeCredits ?? true}
              disabledReason={
                student.billingProvider === "datapart" ? "Nicht verfügbar für Datapart Kunden" : undefined
              }
            >
              <_InvoiceKPI
                amount={totalCreditsAmount}
                name="Gutschriften"
                actionIcon="edit"
                handleModalOpen={() => setOpenFreeCreditDialog(true)}
              />
            </FeatureToggleGreyOut>

            <_DunningStatusKPI
              dunningDisabled={dunningStudent.dunningDisabled}
              dunningPostponedUntil={_getEffectiveDunningPostponedUntil(dunningStudent)}
              handleModalOpen={() => {
                if (isInCollections) {
                  setOpenCollectionsInfoDialog(true);
                  return;
                }
                if (_isDunningActive(dunningStudent)) {
                  openFullDunningDialog();
                  return;
                }
                openActivateDunningDialog();
              }}
            />
          </Card>
          <Row>
            {student.historicalOutstandingPaymentsCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2, mb: "-12px", width: "100%" }}>
                Dieser Fahrschüler war bereits {student.historicalOutstandingPaymentsCount} mal geblockt.
              </Alert>
            )}
          </Row>
          <Box
            sx={{
              display: { xs: "grid", sm: "flex" },
              gridTemplateColumns: "minmax(0, 1fr) auto",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px 16px",
              marginTop: "24px",
              marginBottom: "18px",
            }}
          >
            <Typography sx={{ fontWeight: "bold", fontSize: 19 }}>Rechnungen</Typography>
            <Row
              sx={{
                display: { xs: "contents", sm: "flex" },
                flex: "1 1 425px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 1,
                minWidth: 0,
                "& > .feature-toggle, & > .MuiButton-root": {
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                  width: { xs: "100%", sm: "auto" },
                },
                "& .feature-toggle .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
              }}
            >
              {grants.has("sendEmailSammelquittung") && student.paymentStrategy === "purchaseOnAccount" && (
                <EmailSammelquittungButton student={student} />
              )}
              <FeatureToggleGreyOut
                featureActive={student.featureToggles?.canCreatePayment ?? true}
                disabledReason={
                  student.billingProvider === "datapart" ? "Nicht verfügbar für Datapart Kunden" : undefined
                }
              >
                <CreatePaymentButton />
              </FeatureToggleGreyOut>
              {grants.has("Invoice:createManual") && (
                <FeatureToggleGreyOut
                  featureActive={student.featureToggles?.canCreateManualInvoices ?? true}
                  disabledReason={
                    student.billingProvider === "datapart" ? "Nicht verfügbar für Datapart Kunden" : undefined
                  }
                >
                  <CreateManualInvoiceButton />
                </FeatureToggleGreyOut>
              )}
              {grants.has("Invoice:listPerStudent") && (
                <Box sx={{ display: "flex", gridColumn: { xs: 2, sm: "auto" }, gridRow: { xs: 1, sm: "auto" } }}>
                  <CreateStudentCostOverviewPdfMenu student={student} accounts={accounts} monetaryFlow={monetaryFlow} />
                </Box>
              )}
            </Row>
          </Box>
          <ResponsiveListSurface variant="table">
            <InvoicesList student={student} />
            <Pagination />
          </ResponsiveListSurface>
        </Box>
        <PrepaidCreditsDialog open={openPrepaidCreditDialog} onClose={() => setOpenPrepaidCreditDialog(false)} />
        <AppliedCreditsDialog
          open={openFreeCreditDialog}
          onClose={() => setOpenFreeCreditDialog(false)}
          accounts={accounts}
          monetaryFlow={monetaryFlow}
        />
        <StudentDunningStatusDialog
          {...dunningDialogProps}
          student={dunningStudent}
          dunningDisabled={dunningStudent.dunningDisabled}
          dunningPostponedUntil={dunningStudent.dunningPostponedUntil}
        />
        <ActivateDunningDialog
          {...activateDunningDialogProps}
          student={dunningStudent}
          dunningDisabled={dunningStudent.dunningDisabled}
          dunningPostponedUntil={_getEffectiveDunningPostponedUntil(dunningStudent)}
        />
        <InkassoBlockingDialog open={openCollectionsInfoDialog} onClose={() => setOpenCollectionsInfoDialog(false)} />
      </ListBase>
    </StudentTab>
  );
}

function _DunningStatusKPI(props: {
  dunningDisabled: boolean;
  dunningPostponedUntil?: DateTime;
  handleModalOpen: () => void;
}) {
  const { dunningDisabled, dunningPostponedUntil, handleModalOpen } = props;
  const statusLabel = getDunningStatusLabel(dunningDisabled, dunningPostponedUntil);

  return (
    <Column
      sx={{
        minWidth: "110px",
        gap: "6px",
      }}
    >
      <Box>
        <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>Mahnwesen</Typography>
      </Box>
      <Box>
        <Row alignItems="center" gap={0.25}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>{statusLabel}</Typography>
          </Box>
          <IconButton sx={{ width: "24px", height: "24px" }} onClick={handleModalOpen}>
            <EditIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </Row>
      </Box>
    </Column>
  );
}

function _isDunningActive(student: Student): boolean {
  const postponedUntil = _getEffectiveDunningPostponedUntil(student);
  return !student.dunningDisabled && (!postponedUntil || postponedUntil <= DateTime.now());
}

function _getEffectiveDunningPostponedUntil(student: Student): DateTime | undefined {
  return student.dunningPostponedUntil ?? undefined;
}

function _InvoiceKPI({
  amount,
  name,
  isLoading,
  actionIcon,
  handleModalOpen,
  redForNegativeValues,
}: {
  amount: number;
  name: string;
  isLoading?: boolean;
  actionIcon?: "edit" | "info";
  handleModalOpen?: () => void;
  redForNegativeValues?: boolean;
}) {
  const errorColor = useThemeColor("error").main;
  const formattedAmount = moneyFormatter.format(amount);
  const [actualAmount, currency] = formattedAmount.split(/\s+/);
  const color = redForNegativeValues && amount < 0 ? errorColor : undefined;
  return (
    <Column
      sx={{
        minWidth: "110px",
        gap: "6px",
      }}
    >
      <Box>
        <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>{name}</Typography>
      </Box>
      <Box>
        {isLoading ? (
          <LinearProgress
            timeout={50}
            style={{ margin: "9px 0", width: "3em", display: "inline-block", verticalAlign: "center" }}
          />
        ) : (
          <Row alignItems="center" gap={0.25}>
            <Typography sx={{ color, fontWeight: 600, fontSize: "15px" }}>
              {actualAmount}
              <Box component="span" sx={{ ml: "4px", fontSize: "12px" }}>
                {currency}
              </Box>
            </Typography>

            {actionIcon && (
              <IconButton sx={{ width: "24px", height: "24px" }} onClick={() => handleModalOpen?.()}>
                {actionIcon === "edit" ? (
                  <EditIcon sx={{ width: "16px", height: "16px" }} />
                ) : (
                  <InfoIcon sx={{ width: "16px", height: "16px" }} />
                )}
              </IconButton>
            )}
          </Row>
        )}
      </Box>
    </Column>
  );
}
